/**
 * URL validation utilities for preventing open redirect vulnerabilities
 */

/**
 * Validate that a redirect URL is same-origin (security check)
 * Prevents open redirect attacks by only allowing same-origin URLs
 *
 * @param redirectUrl - The URL to validate (absolute or relative)
 * @returns true if the URL is valid and same-origin, false otherwise
 *
 * @example
 * isValidRedirectUrl('/reload/qrcode') // ✅ true (relative URL)
 * isValidRedirectUrl('https://goodtogo.tw/profile') // ✅ true (same origin)
 * isValidRedirectUrl('https://evil.com/phishing') // ❌ false (different origin)
 * isValidRedirectUrl('javascript:alert(1)') // ❌ false (invalid URL format)
 */
export function isValidRedirectUrl(redirectUrl: string): boolean {
  // Reject empty strings
  if (!redirectUrl || redirectUrl.trim() === '') {
    return false;
  }

  try {
    // Parse URL relative to current origin
    const url = new URL(redirectUrl, window.location.origin);

    // Only allow same-origin URLs
    if (url.origin !== window.location.origin) {
      return false;
    }

    // Reject URLs with userinfo (user:pass@)
    if (url.username || url.password) {
      return false;
    }

    // Reject URLs that don't look like valid paths or full URLs
    // If it doesn't start with /, http://, https://, it's malformed
    if (!redirectUrl.startsWith('/') &&
        !redirectUrl.startsWith('http://') &&
        !redirectUrl.startsWith('https://')) {
      return false;
    }

    return true;
  } catch {
    // Invalid URL format
    return false;
  }
}
